import axios from 'axios'
import { clone, extend, forEach, get, map } from 'lodash'

import * as Promise from 'bluebird'
import util from '../lib/util'
import cu from '../lib/chunkUtil'

// import { data as defaultRubric } from './rubric/new.json'

const ttl = util.ttl

const cchfck = () => 'cchfck=' + Math.random()

class APIError extends Error {
  constructor(message, code) {
    super(arguments)
    this.code = code
    this.message = message
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, APIError)
    }
  }
}

function handleError(error) {
  let code = -1
  let message = 'Unidentified system issue'
  // console.log('API Exception', error)
  try {
    code = error.response.status
    message = get(error, 'response.data.message', message)
  } catch (e) {
    // pass
  }
  throw new APIError(message, code)
}

let extractResponseData = response => {
  if (response.data.data) {
    return response.data.data
  }
  throw new Error('Unexpected response:', response.data)
}

export default class APIBase {
  /**
   *
   * @config host 'https://hostname/', must end with slash
   * @config path 'api/v2/', must end with slash
   * @config postfix '.json'
   */
  constructor(config) {
    this.host = API_URL
    this.path = API_PATH
    this.postfix = ''
    this.preprocessor = function(data) {
      return data
    }
    this.postprocessor = function(data) {
      return data
    }
    this.handleError = handleError
    this.endpoints = {
      AGGREGATE_SCORES: ttl`chunk/${'id'}/rerankmatrix/quality`,
      ANSWERS: ttl`chunk/${'id'}/hypothesis`,
      AUTH_LOGOUT_REFRESH: 'logout/refresh',
      AUTH_LOGOUT: 'logout',
      AUTH_REFRESH: 'auth/refresh',
      AUTH: 'auth',
      CHANGE: ttl`chunk/${'id'}/changes`,
      CHUNK: ttl`chunk/${'id'}`,
      RELATION_CHUNK: ttl`chunk/${'parentId'}/${'parentRelation'}`,
      COMMENTS: ttl`chunk/${'id'}/comment`,
      CURRENT_RUBRIC: 'rubric/current',
      CURRENT_USER: 'user/current',
      EVENT: 'es/event',
      GROUP: 'group',
      GROUP_USERS: ttl`group/${'id'}`,
      MESSAGES: 'es/message',
      MULTIMEDIA: 'multimedia',
      OAUTH: ttl`auth/?src=${'src'}`,
      PHASE: 'chunk/?state=current_week',
      PROBLEM_BY_LABEL: ttl`problem/${'label'}`,
      RELATION: ttl`chunk/${'parentId'}/${'parentRelation'}/${'childId'}`,
      REPORT: 'report/',
      SCORE_CHUNK: ttl`chunk/${'id'}/rate`,
      SIMPLE_QUESTIONS: ttl`chunk/?type=question&state=${'state'}&showDeleted=${'showDeleted'}&preview=default&noattribute=content,scores,parentRelation,parentId,tags,stats&limit=${'limit'}&offset=${'offset'}&sort=createdAt.desc`,
      SUBSCRIBE: 'es/subscribe',
      SUGGESTIONS: ttl`chunk/${'id'}/suggestedChange`,
      CHILDREN: ttl`chunk/${'id'}/${'parentRelation'}`,
      USER_STATS: ttl`user/${'id'}/stats`,
      USER: ttl`user/${'id'}`,
      USERS: 'user/',
      VERSION: 'version'
    }

    extend(this, config)
  }

  makeURL(endpoint) {
    let url = this.host + this.path + endpoint + this.postfix
    return url + (url.indexOf('?') > -1 ? '&' : '?') + cchfck()
  }

  getServerTime(params) {
    params = params || {}
    let me = this
    let options = params.options
    return axios
      .get(me.makeURL(me.endpoints.VERSION), options)
      .then(function(res) {
        return new Date(res.headers.date).getTime()
      })
      .catch(function() {
        return new Date().getTime()
      })
  }

  addChunk(params) {
    let me = this
    let args = arguments
    let chunk = params.chunk
    let options = params.options
    let responseType = chunk.responseType
    let chunk2 = clone(me.preprocessor(cu.canonizeChunk(chunk)))

    if (responseType) {
      if (!options.params) {
        options.params = {}
      }
      options.params.msResource = responseType
    }
    if (chunk2.parentId && chunk2.parentRelation) {
      return me.addRelationChunk({ chunk: chunk2, options, isCanonized: true })
    }
    if (!chunk2.parentId || !chunk2.parentRelation) {
      delete chunk2.parentId
      delete chunk2.parentRelation
    }
    return axios
      .post(me.makeURL(me.endpoints.CHUNK(chunk2)), chunk2, options)
      .then(function(res) {
        return me.postprocessor(cu.normalizeChunk(res.data.data), args)
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  /**
   * Create chunk based on relation
   * @param  {object}  params.chunk   - chunk object
   * @param  {object}  params.options - request options, e.g. headers, etc.
   * @param  {boolean} params.isCanonized
   * @return {Promise}
   */
  async addRelationChunk(params) {
    let me = this
    let args = arguments
    let chunk = params.isCanonized
      ? clone(params.chunk)
      : clone(me.preprocessor(cu.canonizeChunk(params.chunk)))
    const options = params.options
    if (!chunk.parentId || !chunk.parentRelation) {
      throw new Error(
        `Expect \`parentId\` and \`parentRelation\`, but got ${
          chunk.parentId
        } and ${chunk.parentRelation}`
      )
    }
    const url = me.makeURL(me.endpoints.RELATION_CHUNK(chunk))
    delete chunk.parentId
    delete chunk.parentRelation
    try {
      let response = await axios.post(url, chunk, options)
      response = extractResponseData(response)
      response = me.postprocessor(cu.normalizeChunk(response), args)
      return response
    } catch (error) {
      me.handleError(error)
    }
  }

  /**
   * Get chunk by id
   * @param  {string}  params.id - chunk id
   * @return {Promise}
   */
  async getChunk(params) {
    let me = this
    let args = arguments
    let options = params.options

    try {
      let response = await axios.get(
        me.makeURL(me.endpoints.CHUNK(params)),
        options
      )
      response = extractResponseData(response)
      return me.postprocessor(cu.normalizeChunk(response), args)
    } catch (err) {
      me.handleError(err)
    }
  }

  getProblemByLabel(params) {
    let me = this
    let options = params.options
    return axios
      .get(me.makeURL(me.endpoints.PROBLEM_BY_LABEL(params)), options)
      .then(function(res) {
        return res.data
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  async getVersion(params) {
    params = params || {}
    let me = this
    let options = params.options
    return axios
      .get(me.makeURL(me.endpoints.VERSION), options)
      .then(function(res) {
        return res.data
      })
      .catch(function(error) {
        me.handleError(error)
        throw error
      })
  }

  async getAggScores(params) {
    let me = this
    let chunk = params.chunk
    let options = params.options
    return axios
      .get(me.makeURL(me.endpoints.AGGREGATE_SCORES(chunk)), options)
      .then(function(res) {
        let data = res.data
        if (data.success) {
          return data.data
        }
        throw new Error('Empty scores')
      })
      .catch(function(error) {
        me.handleError(error)
        throw error
      })
  }

  async updateAggScores(params) {
    let me = this
    let chunk = params.chunk
    let options = params.options
    return axios
      .put(me.makeURL(me.endpoints.AGGREGATE_SCORES(chunk)), null, options)
      .then(function(res) {
        let data = res.data
        if (data.success) {
          return data.data
        }
        throw new Error('Empty scores')
      })
      .catch(function(error) {
        me.handleError(error)
        throw error
      })
  }

  addUser(params) {
    let me = this
    let args = arguments
    let user = params.user
    let options = params.options
    return axios
      .post(me.makeURL(me.endpoints.USER()), user, options)
      .then(function(res) {
        return me.postprocessor(res.data, args)
      })
  }

  /** { "rate": {
  /     "argumentRating": 20,
  /     "maxLikelihood": 20,
  /     "midLikelihood": 20,
  /     "minLikelihood": 20
  /   }
  } **/

  updateScores(params) {
    let me = this
    let options = params.options
    let value = params.value

    return axios
      .put(me.makeURL(me.endpoints.SCORE_CHUNK(params)), value, options)
      .then(function(res) {
        return res.data.data // scores
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  // {
  //   "data": {
  //     "crowd": {
  //       "argumentRating": {
  //         "50": 1
  //       },
  //       "maxLikelihood": {
  //         "20": 1
  //       },
  //       "midLikelihood": {
  //         "20": 1
  //       },
  //       "minLikelihood": {
  //         "20": 1
  //       }
  //     },
  //     "mine": {
  //       "argumentRating": 50,
  //       "maxLikelihood": 20,
  //       "midLikelihood": 20,
  //       "minLikelihood": 20
  //     }
  //   },
  //   "success": true
  // }

  async getScores(params) {
    let me = this
    let { id, options } = params

    return axios
      .get(me.makeURL(me.endpoints.SCORE_CHUNK({ id })), options)
      .then(function(res) {
        return res.data.data // scores
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  getCurrentRubric(params) {
    let me = this
    let options = params.options
    let url = me.makeURL(me.endpoints.CURRENT_RUBRIC)

    return axios
      .get(url, options)
      .then(function(res) {
        return res.data.data
        // return Promise.resolve(defaultRubric)
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  /**
   * Update chunk
   * @param  {object}  params.chunk   - chunk object
   * @param  {object}  params.options - axios options
   * @return {Promise}                - updated chunk object
   */
  async updateChunk(params) {
    let me = this
    let { chunk, options } = params
    try {
      const url = this.makeURL(me.endpoints.CHUNK(chunk))
      chunk = cu.canonizeChunk(chunk)
      delete chunk.id
      let res = await axios.put(url, chunk, options)
      res = extractResponseData(res)
      res = this.postprocessor(cu.normalizeChunk(res))
      return res
    } catch (err) {
      this.handleError(err)
    }
  }

  /**
   * Get current user info
   * @param  {string}  params.token - access token
   * @return {Promise} current user object
   */
  async getCurrentUser(params) {
    let _self = this
    const { token } = params
    const options = {
      headers: {
        Authorization: token
      }
    }
    try {
      let response = await axios.get(
        _self.makeURL(_self.endpoints.CURRENT_USER),
        options
      )
      response = extractResponseData(response)
      return response
    } catch (error) {
      _self.handleError(error)
    }
  }

  /**
   * Update user info
   * @param  {object}  params.object   - user object
   * @param  {object}  params.updates  - updates for user object
   * @param  {object}  params.options  - request options
   * @return {Promise} return updated user object
   */
  async updateUser(params) {
    let _self = this
    let { object, updates, options } = params
    try {
      await axios.put(
        _self.makeURL(_self.endpoints.USER(object)),
        updates,
        options
      )
      const response = extend(object, updates)
      return response
    } catch (error) {
      _self.handleError(error)
    }
  }

  getUserStats(params) {
    let me = this
    let args = arguments
    return axios
      .get(me.makeURL(me.endpoints.USER_STATS(params.user)), params.options)
      .then(function(res) {
        return me.postprocessor(res.data, args)
      })
  }
  /**
   * Get all users
   * @param   {object} params
   * @param   {string} params.options - request header
   * @returns {Array<object>}         - returns a list of users
   */
  async getUsers(params) {
    let _self = this
    const { options } = params
    try {
      let response = await axios.get(
        _self.makeURL(_self.endpoints.USERS),
        options
      )
      response = extractResponseData(response)
      return response
    } catch (error) {
      _self.handleError(error)
    }
  }

  /**
   * Get simplified problems payload based on the params
   * @param  {number}   params.limit        - problem pagination limit
   * @param  {number}   params.offset       - problem pagination offset
   * @param  {boolean}  params.showDeleted  - if return deleted problem
   * @param  {string}   params.state        - problem state
   * @param  {string}   [params.userGroup]  - user group code
   * @return {Promise}
   */
  async getSimpleQuestions(params) {
    let me = this
    const userGroup = params.userGroup
    const url = me.makeURL(
      `${me.endpoints.SIMPLE_QUESTIONS(params)}${
        userGroup ? `&userGroup=${userGroup}` : ''
      }`
    )
    try {
      let response = await axios.get(url, params.options)
      response = extractResponseData(response)
      return response
    } catch (error) {
      me.handleError(error)
    }
  }

  async getComments(chunk, options) {
    let me = this
    try {
      const res = await axios.get(
        me.makeURL(me.endpoints.COMMENTS(chunk)),
        options
      )
      const data = forEach(res.data.data, answer => {
        return cu.normalizeChunk(answer)
      })
      return data
    } catch (err) {
      me.handleError(err)
    }
  }

  async getAnswers(chunk, options) {
    let me = this
    try {
      const res = await axios.get(
        me.makeURL(me.endpoints.ANSWERS(chunk)),
        options
      )
      const data = me.postprocessor(
        map(res.data.data, answer => {
          return cu.normalizeChunk(answer)
        })
      )
      return data
    } catch (err) {
      me.handleError(err)
    }
  }

  getSuggestions(params) {
    let me = this

    return axios
      .get(me.makeURL(me.endpoints.SUGGESTIONS(params.parent)), params.options)
      .then(function(res) {
        return map(res.data.data, cu.normalizeChunk)
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  /**
   * Get children of a chunk with a specific relation
   *
   * @param params {Object} parameters
   * @param params.id Parent chunk id
   * @param params.parentRelation Relationship
   * @returns {Promise<AxiosResponse>} Normalised chunks
   */
  async getChildren(params) {
    let me = this

    return axios
      .get(
        me.makeURL(
          me.endpoints.CHILDREN({
            id: params.parent.id,
            parentRelation: params.parentRelation
          })
        ),
        params.options
      )
      .then(function(res) {
        return map(res.data.data, cu.normalizeChunk)
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  // ---- modify relationship

  addRelation(params) {
    let me = this
    return axios
      .put(me.makeURL(me.endpoints.RELATION(params)), '', params.options)
      .then(function(res) {
        return true
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  deleteRelation(params) {
    let me = this
    return axios
      .delete(me.makeURL(me.endpoints.RELATION(params)), params.options)
      .then(function(res) {
        return true
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  getUser(cfg) {
    let me = this
    let args = arguments
    return axios.get(me.makeURL(me.endpoints.USER(cfg))).then(function(res) {
      return me.postprocessor(res.data, args)
    })
  }

  getChanges(chunk) {
    let me = this
    let args = arguments
    return axios
      .get(me.makeURL(me.endpoints.CHANGES(chunk)))
      .then(function(res) {
        return me.postprocessor(res.data, args)
      })
  }

  // Authentication

  async getOauthToken(params) {
    let me = this
    try {
      const { data } = await axios.post(me.makeURL(me.endpoints.AUTH), params)
      let token = get(data, 'access_token', null)
      let refreshToken = get(data, 'refresh_token', null)
      let vapidPublicKey = get(data, 'public_key', null)
      if (token) {
        data.token = token
        data.refreshToken = refreshToken
        data.vapidPublicKey = vapidPublicKey
        return data
      }
      return Promise.reject(new Error('Invalid credential'))
    } catch (err) {
      me.handleError(err)
      return Promise.reject(err)
    }
  }

  authenticate(params) {
    let me = this
    const axiosNoInterceptors = axios.create()
    return axiosNoInterceptors
      .post(me.makeURL(me.endpoints.AUTH), params.credential)
      .then(function(res) {
        let data = res.data
        let token = get(data, 'access_token', null)
        let refreshToken = get(data, 'refresh_token', null)
        let vapidPublicKey = get(data, 'public_key', null)
        if (token) {
          data.token = token
          data.refreshToken = refreshToken
          data.vapidPublicKey = vapidPublicKey
          return data
        }
        return Promise.reject(new Error('Invalid credential'))
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  getNewAccessToken(options) {
    let me = this
    let payload = {}
    // Create an instance to avoid intercepting 401 error.
    // Otherwise, this will be in infinite loops if an invalid (or non-existing) refresh token is provided.
    const axiosNoInterceptors = axios.create()
    return axiosNoInterceptors
      .post(me.makeURL(me.endpoints.AUTH_REFRESH), payload, options)
      .then(function(res) {
        let token = get(res, 'data.access_token', null)
        let data = {}
        if (token) {
          data.token = token
          return data
        }
        return Promise.reject(new Error('Invalid credential'))
      })
      .catch(function(error) {
        me.handleError(error)
      })
  }

  async logout(options) {
    let me = this
    const axiosNoInterceptors = axios.create(options) // Do not intercept 401 error
    await axiosNoInterceptors.post(me.makeURL(me.endpoints.AUTH_LOGOUT))
  }

  async logoutRefresh(options) {
    let me = this
    const axiosNoInterceptors = axios.create(options) // Do not intercept 401 error
    await axiosNoInterceptors.post(me.makeURL(me.endpoints.AUTH_LOGOUT_REFRESH))
  }

  // Subscription-related

  createSubscription(params) {
    let me = this
    let payload = params.payload
    let options = params.options

    return axios
      .post(me.makeURL(me.endpoints.SUBSCRIBE), payload, options)
      .then(function(res) {
        return res.data
      })
      .catch(function(error) {
        return Promise.reject(error)
      })
  }

  updateSubscription(params) {
    let me = this
    let options = params.options
    let payload = params.payload

    return axios
      .put(me.makeURL(me.endpoints.SUBSCRIBE), payload, options)
      .then(function(res) {
        return res.data
      })
      .catch(function(error) {
        return Promise.reject(error)
      })
  }

  revokeSubscription(params) {
    let me = this
    let options = params.options

    return axios
      .delete(me.makeURL(me.endpoints.SUBSCRIBE), options)
      .then(function(res) {
        return res.data
      })
      .catch(function(error) {
        return Promise.reject(error)
      })
  }

  // Event API

  postEvent({ payload, options }) {
    let me = this
    return axios
      .post(me.makeURL(me.endpoints.EVENT), payload, options)
      .then(function(res) {
        if (res) {
          return res.data
        }
      })
      .catch(function(error) {
        return Promise.reject(error)
      })
  }

  // Report API

  async generateReport(params) {
    let me = this
    let { options, payload } = params

    try {
      const response = await axios.post(
        me.makeURL(me.endpoints.REPORT),
        payload,
        options
      )
      return response.data.reportId
    } catch (err) {
      throw err
    }
  }

  /**
   * Get all user groups information
   * @param  {object} params.options - request options
   * @return {Promise} a list of user groups
   */
  async getAllUserGroups(params) {
    let me = this
    let { options } = params

    try {
      let response = await axios.get(me.makeURL(me.endpoints.GROUP), options)
      response = extractResponseData(response)
      return response
    } catch (err) {
      throw err
    }
  }

  /**
   * Upload file
   * @param  {object}  params.formData - the file needs to be uploaded
   * @return {Promise}
   */
  async uploadFile(params) {
    let me = this
    const { options, payload } = params
    try {
      const response = await axios.post(
        me.makeURL(me.endpoints.MULTIMEDIA),
        payload,
        options
      )
      return response.data.url
    } catch (err) {
      throw err
    }
  }

  /**
   * Get users belongs to one group
   * @param   {String} id      - group id
   * @param   {Object} options - request options
   * @returns {Promise<Array>} - returns a list of users
   */
  async getGroupUsers({ id, options }) {
    const _self = this
    const url = _self.makeURL(_self.endpoints.GROUP_USERS({ id }))
    const response = await axios.get(url, options)
    return response.data.data.users
  }
}
